import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import CustomMultiSelect from '../../CustomMultiSelect';
import AdvanceDateRangePicker from '../../common/AdvanceDateRangePicker';
import { useAdvancedDateRangePicker } from '../../../hooks/useAdvancedDateRangePicker';
import moment from 'moment';
import { isClient, individualOrdersOption } from '../../../helpers/global';

const defaultDateFilter = {
  startDate: null,
  endDate: null,
  key: 'selection'
};

const getFilteredDateRange = (filterMetadata, key) => {
  const filter = filterMetadata.find(f => f.key === key);
  return filter?.values;
};

const FilterServices = ({ filterMetadata, onFilterMetadataChange, onFilterServices }) => {
  const isClientRole = isClient(localStorage.getItem('user-role'));

  const {
    dateRange: deliveryDateRange,
    setDateRange: setDeliveryDateRange,
    handleNavigateDate: handleDeliveryNavigateDate
  } = useAdvancedDateRangePicker(defaultDateFilter);

  const {
    dateRange: orderDateRange,
    setDateRange: setOrderDateRange,
    handleNavigateDate: handleOrderNavigateDate
  } = useAdvancedDateRangePicker(defaultDateFilter);

  const {
    dateRange: cancelledDateRange,
    setDateRange: setCancelledDateRange,
    handleNavigateDate: handleCancelledNavigateDate
  } = useAdvancedDateRangePicker(defaultDateFilter);

  const {
    dateRange: closingDateRange,
    setDateRange: setClosingDateRange,
    handleNavigateDate: handleClosingNavigateDate
  } = useAdvancedDateRangePicker(defaultDateFilter);

  useEffect(() => {
    if (!filterMetadata) return;

    const syncDeliveredDateRange = getFilteredDateRange(filterMetadata, 'dateDelivered');
    if (syncDeliveredDateRange) {
      setDeliveryDateRange(prev => ({
        ...prev,
        ...syncDeliveredDateRange
      }));
    }

    const syncCreatedDateRange = getFilteredDateRange(filterMetadata, 'createdAt');
    if (syncCreatedDateRange) {
      setOrderDateRange(prev => ({
        ...prev,
        ...syncCreatedDateRange
      }));
    }

    const syncCancelledDateRange = getFilteredDateRange(filterMetadata, 'dateCancelled');
    if (syncCancelledDateRange) {
      setCancelledDateRange(prev => ({
        ...prev,
        ...syncCancelledDateRange
      }));
    }

    const syncClosingDateRange = getFilteredDateRange(filterMetadata, 'closingDate');
    if (syncClosingDateRange) {
      setClosingDateRange(prev => ({
        ...prev,
        ...syncClosingDateRange
      }));
    }
  }, [filterMetadata]);

  const findFilterValuesByParent = parentKey => {
    return filterMetadata?.find(filterService => filterService?.key === parentKey);
  };

  const handleFilterProcessorSelect = (key, values) => {
    // Use map to preserve the original order of filters
    onFilterMetadataChange(prevFilterMetadata => {
      return prevFilterMetadata.map(filter =>
        filter.key === key ? { ...filter, values: values.map(value => value.value) } : filter
      );
    });
  };

  const handleDateRangeChange = (key, ranges) => {
    if (key === 'dateDelivered') {
      setDeliveryDateRange(prev => ({
        ...prev,
        ...ranges.selection,
        rangeType: ranges.selection.rangeType || prev.rangeType
      }));
    } else if (key === 'createdAt') {
      setOrderDateRange(prev => ({
        ...prev,
        ...ranges.selection,
        rangeType: ranges.selection.rangeType || prev.rangeType
      }));
    } else if (key === 'dateCancelled') {
      setCancelledDateRange(prev => ({
        ...prev,
        ...ranges.selection,
        rangeType: ranges.selection.rangeType || prev.rangeType
      }));
    } else if (key === 'closingDate') {
      setClosingDateRange(prev => ({
        ...prev,
        ...ranges.selection,
        rangeType: ranges.selection.rangeType || prev.rangeType
      }));
    }

    const startDate = ranges.selection.startDate
      ? moment(ranges.selection.startDate)
          .startOf('day')
          .toISOString()
      : null;
    const endDate = ranges.selection.endDate
      ? moment(ranges.selection.endDate)
          .endOf('day')
          .toISOString()
      : null;

    const updatedFilterMetadata = filterMetadata.map(filter =>
      filter.key === key ? { ...filter, values: { startDate, endDate } } : filter
    );

    onFilterMetadataChange(updatedFilterMetadata);
  };

  const getDateFilterValue = key => {
    // Return the appropriate date range state based on the key
    switch (key) {
      case 'dateDelivered':
        return deliveryDateRange;
      case 'createdAt':
        return orderDateRange;
      case 'dateCancelled':
        return cancelledDateRange;
      case 'closingDate':
        return closingDateRange;
    }
  };

  const clearDateFilter = key => {
    const emptyRange = {
      startDate: null,
      endDate: null,
      key: 'selection',
      rangeType: 'All Time'
    };

    if (key === 'dateDelivered') {
      setDeliveryDateRange(emptyRange);
    } else if (key === 'createdAt') {
      setOrderDateRange(emptyRange);
    } else if (key === 'dateCancelled') {
      setCancelledDateRange(emptyRange);
    } else if (key === 'closingDate') {
      setClosingDateRange(emptyRange);
    }

    // Clear filterMetadata - find and update existing filter
    const updatedFilterMetadata = filterMetadata.map(filter =>
      filter.key === key ? { ...filter, values: { startDate: null, endDate: null } } : filter
    );

    onFilterMetadataChange(updatedFilterMetadata);
  };

  const getHandleNavigateDate = key => {
    switch (key) {
      case 'dateDelivered':
        return (rangeType, direction) => {
          setDeliveryDateRange(prev => ({
            ...prev,
            rangeType: rangeType
          }));

          handleDeliveryNavigateDate(rangeType, direction);
        };
      case 'createdAt':
        return (rangeType, direction) => {
          setOrderDateRange(prev => ({
            ...prev,
            rangeType: rangeType
          }));

          handleOrderNavigateDate(rangeType, direction);
        };
      case 'dateCancelled':
        return (rangeType, direction) => {
          setCancelledDateRange(prev => ({
            ...prev,
            rangeType: rangeType
          }));

          handleCancelledNavigateDate(rangeType, direction);
        };
      case 'closingDate':
        return (rangeType, direction) => {
          setClosingDateRange(prev => ({
            ...prev,
            rangeType: rangeType
          }));

          handleClosingNavigateDate(rangeType, direction);
        };
      default:
        return () => {};
    }
  };

  const handleClearAll = () => {
    const emptyRange = {
      startDate: null,
      endDate: null,
      key: 'selection',
      rangeType: 'All Time'
    };

    setDeliveryDateRange(emptyRange);
    setOrderDateRange(emptyRange);
    setCancelledDateRange(emptyRange);
    setClosingDateRange(emptyRange);
    onFilterMetadataChange([]);
  };

  return (
    <div className="d-flex row pl-8 w-100 m-0" style={{ gap: 4 }}>
      {filterMetadata?.map(filter => (
        <div key={filter.key} className="mb-1" style={{ width: '100%' }}>
          {filter.type === 'date' ? (
            <div className="d-flex flex-column w-100" style={{ position: 'relative' }}>
              <div className="d-flex justify-content-between align-items-center ml-2">
                <label className="fw-bold mb-2 smallFont fw-bold text-dark">{filter.label}</label>
                {getDateFilterValue(filter.key)?.startDate && getDateFilterValue(filter.key)?.endDate && (
                  <Button
                    variant="secondary"
                    className="text-white px-1 py-0"
                    size="sm"
                    onClick={() => clearDateFilter(filter.key)}
                  >
                    Clear
                  </Button>
                )}
              </div>
              <div className="ml-2" style={{ position: 'relative', zIndex: 1000 }}>
                <AdvanceDateRangePicker
                  value={getDateFilterValue(filter.key)}
                  onChange={ranges => handleDateRangeChange(filter.key, ranges)}
                  className="w-100"
                  extraStyleForSlideBarFilter={true}
                  showNavigator={false}
                  rangeType={getDateFilterValue(filter.key)?.rangeType || 'All Time'}
                  handleNavigateDate={getHandleNavigateDate(filter.key)}
                  isFloating={true}
                  topOffset={isClientRole ? 200 : 0}
                  allowAllTime={false}
                />
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column">
              <CustomMultiSelect
                id={filter.key}
                onChange={values => handleFilterProcessorSelect(filter.key, values)}
                selectedOptions={findFilterValuesByParent(filter.key)?.values || []}
                options={filter.key === 'organization' ? [individualOrdersOption, ...filter.options] : filter.options}
                label={filter.label}
                placeholder={`Select ${filter.label}`}
              />
            </div>
          )}
        </div>
      ))}

      <div className="my-2 w-100 d-flex justify-content-end">
        {/* <Button variant="outline-secondary" className="mr-2" onClick={() => handleClearAll()}>
          Clear All
        </Button> */}
        <Button variant="primary" onClick={onFilterServices}>
          Apply
        </Button>
      </div>
    </div>
  );
};

export default FilterServices;
